import React, { useEffect, useState } from 'react';
import { landingAPI, uploadAPI, BADGE_COLORS, SITE_NAMES, LANDING_DEFAULTS } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { Save, Upload, Plus, Trash2, X, Image as ImageIcon } from 'lucide-react';

const emptySettings = () => ({
  icon_emoji: '',
  icon_image_url: null,
  title: '',
  subtitle: '',
  badges: []
});

export const LandingAdmin = () => {
  const [data, setData] = useState({ ms: emptySettings(), ds: emptySettings() });
  const [activeSite, setActiveSite] = useState('ms');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    landingAPI.get()
      .then(res => {
        setData({
          ms: { ...LANDING_DEFAULTS.ms, ...(res.data?.ms || {}) },
          ds: { ...LANDING_DEFAULTS.ds, ...(res.data?.ds || {}) }
        });
      })
      .catch((e) => {
        // Fallback — bez toastu pokud je to 404 (backend ještě bez endpointu), jen načti defaulty
        setData({
          ms: { ...LANDING_DEFAULTS.ms },
          ds: { ...LANDING_DEFAULTS.ds }
        });
        if (e?.response?.status !== 404) {
          toast.error('Nepodařilo se načíst nastavení rozcestníku');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const update = (siteKey, key, value) => {
    setData(prev => ({ ...prev, [siteKey]: { ...prev[siteKey], [key]: value } }));
  };

  const updateBadge = (siteKey, index, key, value) => {
    setData(prev => {
      const badges = [...(prev[siteKey].badges || [])];
      badges[index] = { ...badges[index], [key]: value };
      return { ...prev, [siteKey]: { ...prev[siteKey], badges } };
    });
  };

  const addBadge = (siteKey) => {
    setData(prev => ({
      ...prev,
      [siteKey]: {
        ...prev[siteKey],
        badges: [...(prev[siteKey].badges || []), { label: '', color_key: 'blue' }]
      }
    }));
  };

  const removeBadge = (siteKey, index) => {
    setData(prev => {
      const badges = (prev[siteKey].badges || []).filter((_, i) => i !== index);
      return { ...prev, [siteKey]: { ...prev[siteKey], badges } };
    });
  };

  const handleIconUpload = async (siteKey, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAPI.upload(file);
      update(siteKey, 'icon_image_url', res.data.url);
      toast.success('Obrázek nahrán');
    } catch (e) {
      toast.error('Nepodařilo se nahrát obrázek');
    } finally {
      setUploading(false);
    }
  };

  const removeIcon = (siteKey) => update(siteKey, 'icon_image_url', null);

  const save = async (siteKey) => {
    setSaving(true);
    try {
      const current = data[siteKey] || {};
      // Sestav čistý payload — never mutuj state, defaults pro povinná pole
      const payload = {
        icon_emoji: current.icon_emoji || '',
        icon_image_url: current.icon_image_url || null,
        title: (current.title || '').trim() || (siteKey === 'ms' ? 'MŠ Cestička' : 'Chaloupka na vršku'),
        subtitle: current.subtitle || '',
        badges: (current.badges || [])
          .filter(b => b && b.label?.trim())
          .map(b => ({
            label: b.label.trim(),
            color_key: BADGE_COLORS[b.color_key] ? b.color_key : 'blue'
          }))
      };
      const res = await landingAPI.update(siteKey, payload);
      // Sync state s návratovou hodnotou ze serveru
      setData(prev => ({ ...prev, [siteKey]: { ...prev[siteKey], ...res.data } }));
      toast.success(`Rozcestník ${SITE_NAMES[siteKey]} uložen`);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (e?.response?.status === 404) {
        toast.error('Backend ještě nemá tuto funkci. Redeployujte produkci.');
      } else if (Array.isArray(msg)) {
        toast.error('Chyba ve formuláři: ' + msg.map(d => d.msg).join('; '));
      } else {
        toast.error(typeof msg === 'string' ? msg : 'Nepodařilo se uložit');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">Rozcestník</h1>
        <p className="text-[#64748B]">Úprava úvodní stránky — ikona, popisek a štítky pro každou školku</p>
      </div>

      <Tabs value={activeSite} onValueChange={setActiveSite}>
        <TabsList className="rounded-full">
          <TabsTrigger value="ms" data-testid="landing-tab-ms" className="rounded-full">{SITE_NAMES.ms}</TabsTrigger>
          <TabsTrigger value="ds" data-testid="landing-tab-ds" className="rounded-full">{SITE_NAMES.ds}</TabsTrigger>
        </TabsList>

        {['ms', 'ds'].map(siteKey => {
          const s = data[siteKey];
          return (
            <TabsContent key={siteKey} value={siteKey}>
              <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
                {/* Icon */}
                <div className="space-y-2">
                  <Label>Ikona školky</Label>
                  <div className="flex items-start gap-4 flex-wrap">
                    <div className="w-24 h-24 rounded-3xl bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {s.icon_image_url ? (
                        <img src={s.icon_image_url} alt="ikona" className="w-full h-full object-contain p-2" />
                      ) : (
                        <span className="text-4xl leading-none">{s.icon_emoji || '🏫'}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-[240px] space-y-3">
                      <div>
                        <Label className="text-xs text-[#64748B]">Emoji (pokud není nahrán obrázek)</Label>
                        <Input
                          value={s.icon_emoji || ''}
                          onChange={(e) => update(siteKey, 'icon_emoji', e.target.value)}
                          placeholder="🏫"
                          className="rounded-xl"
                          data-testid={`landing-${siteKey}-emoji`}
                        />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleIconUpload(siteKey, e.target.files?.[0])}
                          />
                          <Button type="button" variant="outline" disabled={uploading} className="rounded-full" asChild>
                            <span>
                              <Upload size={14} className="mr-2" />
                              {uploading ? 'Nahrávání...' : s.icon_image_url ? 'Změnit obrázek' : 'Nahrát obrázek'}
                            </span>
                          </Button>
                        </label>
                        {s.icon_image_url && (
                          <Button type="button" variant="outline" onClick={() => removeIcon(siteKey)} className="rounded-full text-red-500 hover:text-red-700" data-testid={`landing-${siteKey}-remove-img`}>
                            <X size={14} className="mr-2" /> Odebrat obrázek
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-[#64748B]">Obrázek se automaticky přizpůsobí (čtvercová ikona). Doporučená velikost 200×200 px.</p>
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div className="space-y-2">
                  <Label>Název školky</Label>
                  <Input
                    value={s.title || ''}
                    onChange={(e) => update(siteKey, 'title', e.target.value)}
                    placeholder="MŠ Cestička"
                    className="rounded-xl"
                    data-testid={`landing-${siteKey}-title`}
                  />
                </div>

                {/* Subtitle */}
                <div className="space-y-2">
                  <Label>Popisek</Label>
                  <Input
                    value={s.subtitle || ''}
                    onChange={(e) => update(siteKey, 'subtitle', e.target.value)}
                    placeholder="Mateřská škola pro děti 3–6 let"
                    className="rounded-xl"
                    data-testid={`landing-${siteKey}-subtitle`}
                  />
                </div>

                {/* Badges */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Štítky</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => addBadge(siteKey)} className="rounded-full" data-testid={`landing-${siteKey}-add-badge`}>
                      <Plus size={14} className="mr-1" /> Přidat štítek
                    </Button>
                  </div>
                  {(s.badges || []).length === 0 && (
                    <div className="text-sm text-[#64748B] py-4 text-center border border-dashed border-slate-200 rounded-xl">
                      <ImageIcon className="mx-auto mb-2 text-slate-300" size={28} />
                      Zatím žádné štítky. Klikněte na „Přidat štítek".
                    </div>
                  )}
                  <div className="space-y-3">
                    {(s.badges || []).map((b, i) => {
                      const colors = BADGE_COLORS[b.color_key] || BADGE_COLORS.blue;
                      return (
                        <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl" data-testid={`landing-${siteKey}-badge-${i}`}>
                          <span className="px-3 py-1 rounded-full text-sm font-semibold flex-shrink-0" style={{ backgroundColor: colors.bg, color: colors.text }}>
                            {b.label || 'Náhled'}
                          </span>
                          <Input
                            value={b.label || ''}
                            onChange={(e) => updateBadge(siteKey, i, 'label', e.target.value)}
                            placeholder="Text štítku"
                            className="rounded-lg flex-1 min-w-[120px]"
                            data-testid={`landing-${siteKey}-badge-${i}-label`}
                          />
                          <div className="flex gap-1 flex-wrap">
                            {Object.entries(BADGE_COLORS).map(([key, c]) => (
                              <button
                                key={key}
                                type="button"
                                onClick={() => updateBadge(siteKey, i, 'color_key', key)}
                                title={c.label}
                                className={`w-7 h-7 rounded-full border-2 transition-all ${b.color_key === key ? 'border-[#2D3748] scale-110' : 'border-transparent'}`}
                                style={{ backgroundColor: c.bg }}
                                data-testid={`landing-${siteKey}-badge-${i}-color-${key}`}
                              >
                                <span className="block w-3 h-3 rounded-full mx-auto" style={{ backgroundColor: c.text }} />
                              </button>
                            ))}
                          </div>
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeBadge(siteKey, i)} className="text-red-500 hover:text-red-700" data-testid={`landing-${siteKey}-badge-${i}-remove`}>
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="pt-2">
                  <Button onClick={() => save(siteKey)} disabled={saving} className="rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid={`landing-${siteKey}-save`}>
                    <Save size={16} className="mr-2" />
                    {saving ? 'Ukládání...' : `Uložit ${SITE_NAMES[siteKey]}`}
                  </Button>
                </div>
              </div>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
};

export default LandingAdmin;
